/**
 * Vault file areas — the client's mirror of `@fias/db-types` vault-areas.ts.
 *
 *   - `documents`   — the user's own tree (the Holder root and every folder
 *                     outside the two system roots).
 *   - `arche-saves` — `/Arche Saves/**`: files arches delivered automatically.
 *                     View, rename, delete and move OUT; never in.
 *   - `my-fias`     — `/My-Fias/**`: arches' own internal files. View and
 *                     delete; nothing else.
 *
 * The client does not depend on `@fias/db-types`, so this is a copy — kept in
 * step by `tests/architecture/vault-area-policy-client-parity.test.ts`, which
 * runs both over every case. The SERVER is the boundary and re-decides every
 * operation; this only lets the UI offer what will work and explain what
 * won't before the user tries.
 */

export type VaultArea = 'documents' | 'arche-saves' | 'my-fias';

export const MY_FIAS_ROOT = '/My-Fias';
export const ARCHE_SAVES_ROOT = '/Arche Saves';
/** Where email attachments land (`VAULT_ARCHE_SAVES_FOLDER_PATHS.EMAIL_ATTACHMENTS`).
 *  A file here stays tied to its email — deleting the email deletes it,
 *  wherever it has been moved — so taking one into Documents makes a copy. */
export const EMAIL_ATTACHMENTS_PATH = `${ARCHE_SAVES_ROOT}/Email Attachments`;

function isAtOrUnder(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`);
}

/** Whether a folder path is `root` or inside it (`null` = the Holder root). */
export function isFolderPathAtOrUnder(
  folderPath: string | null | undefined,
  root: string,
): boolean {
  return !!folderPath && isAtOrUnder(folderPath, root);
}

/** The area a FOLDER path belongs to; `null` is the Holder root (Documents). */
export function vaultAreaOfPath(folderPath: string | null | undefined): VaultArea {
  if (!folderPath) return 'documents';
  if (isAtOrUnder(folderPath, MY_FIAS_ROOT)) return 'my-fias';
  if (isAtOrUnder(folderPath, ARCHE_SAVES_ROOT)) return 'arche-saves';
  return 'documents';
}

/** Operations a person can attempt in an area. */
export type VaultAreaOperation =
  | 'view'
  | 'upload'
  | 'create-folder'
  | 'modify-folder'
  | 'rename-file'
  | 'change-sensitivity'
  | 'delete-file';

/**
 * Whether `op` is allowed in `area` for a member who can write (the owner of a
 * personal space; an admin in a workspace). Role refinements — a viewer can
 * only look; deleting My-Fias files in a workspace needs an admin — are the
 * server's to apply.
 */
export function vaultAreaAllows(area: VaultArea, op: VaultAreaOperation): boolean {
  if (op === 'view' || area === 'documents') return true;
  switch (op) {
    case 'upload':
    case 'create-folder':
    case 'modify-folder':
      return false;
    case 'rename-file':
    case 'change-sensitivity':
      return area === 'arche-saves';
    case 'delete-file':
      return true;
  }
}

export type VaultAreaMoveRefusal = 'NOT_MOVABLE' | 'AREA_READ_ONLY';

/** `null` when a file may move from `from` to `to`; otherwise why not. */
export function vaultAreaMoveRefusal(from: VaultArea, to: VaultArea): VaultAreaMoveRefusal | null {
  if (from === 'my-fias') return 'NOT_MOVABLE';
  if (to === 'my-fias' || to === 'arche-saves') return 'AREA_READ_ONLY';
  return null;
}

/** `/My-Fias/Arches/<archeId>[/…]` — the arche whose namespace `path` is in. */
const ARCHE_NAMESPACE_RE = /^\/My-Fias\/Arches\/([^/]+)(?:\/|$)/;

export function archeNamespaceOfPath(path: string | null | undefined): string | null {
  if (!path) return null;
  return ARCHE_NAMESPACE_RE.exec(path)?.[1] ?? null;
}
